/**
 * AS PALAVRAS DO MOTOR DE FORMULÁRIOS — as últimas do painel que só existiam
 * em português.
 *
 * ── O QUE ESTAVA ERRADO ───────────────────────────────────────────────────
 *
 * `src/dynamic/` desenha TODA tela de cadastro do produto: a ficha de um
 * registro, o card de um funil, o formulário que um blueprint acabou de criar.
 * Não tinha dicionário nenhum. Um painel em inglês pedia um campo obrigatório
 * com "Nome é obrigatório", recusava um telefone com "inclua o código do país
 * com + na frente", e a tabela vazia dizia "Ainda não há pedidos" — em telas
 * onde tudo à volta estava em inglês, porque tudo à volta tinha dicionário.
 *
 * Nada disto aparecia no `verify/keys.mjs`: ele conta chaves que EXISTEM e não
 * frases que nunca foram chaves. É o furo que a auditoria do inglês encontrou e
 * é o maior deles em número de frases.
 *
 * ── POR QUE EM `shared/` E NÃO EM `dynamic/i18n/` ─────────────────────────
 *
 * Porque quem diz estas frases não é um componente. `widgets/contract.ts` é um
 * mapa de validadores puros e `resolvers/list-view.ts` é uma função sobre
 * metadados — nenhum dos dois tem um `setup` onde chamar `useI18n`, e passar um
 * `t` por parâmetro atravessaria seis assinaturas para o mesmo fim. Eles pedem
 * a frase à instância global (`i18n.global.t`), e uma instância global só
 * responde por frases que estão na raiz dela — que é aqui, ao lado de
 * `common.ts` e `errors.ts`, pelo mesmo motivo pelo qual aquelas duas estão.
 *
 * ── `dynamicForm.` NO PREFIXO ─────────────────────────────────────────────
 *
 * A raiz é partilhada por toda a aplicação e uma chave chamada `required` ali
 * seria uma palavra que qualquer tela pode pisar sem saber. O prefixo diz de
 * quem é.
 */
export const dynamicFormMessages = {
	en: {
		dynamicForm: {
			snippetInsert: "Insert variable",
			snippetMissing: "Complete these variables before sending: {variables}",
			snippetVariable: {
				name: "Contact name",
				phone: "Contact phone",
				email: "Contact email",
				attendant: "Attendant name",
				channel: "Channel name",
			},
			stage: "Stage",
			stageHint: "The same stage as on the funnel.",
			/** `{label}` é o rótulo do campo, tal como o construtor o escreveu. */
			required: "{label} is required",
			invalidEmail: "that does not look like an email",
			invalidPhone: "include the country code with a + in front (a national number is ambiguous)",
			invalidUrl: "include http:// or https://",
			invalidTime: "use the format HH:MM",
			wholeNumber: "use a whole number, without decimals",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "write the number as in {example}",
			invalidJson: "invalid JSON",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "choose a moment still to come",
			save: "Save",
			create: "Create",
			cancel: "Cancel",
			/** O que um widget diz enquanto as opções de uma consulta não chegaram. */
			loadingOptions: "loading the options…",
			optionsUnavailable: "the options for this question come from a query, unavailable here",
			uploadUnavailable: "uploading is not available on this screen",
			uploadFailed: "could not upload",
			fileMoveEarlier: "move earlier (the first one is the cover)",
			fileMoveLater: "move later",
			signatureFailed: "could not produce the image",
			signatureSaveFailed: "could not save",
			lookupFailed: "could not search",
			subformLoadFailed: "could not load the items",
			addChildren: "+ Add {name} now (optional)",
			moreDetails: "More details",
			computedFrom: "Computed from {from}",
			computedAuto: "Computed automatically",
			copiedFrom: "copied from {link}",
			subformAddFailed: "could not add",
			empty: {
				/**
				 * As três razões de uma tabela estar vazia, que se leem todas como "os
				 * dados sumiram" se forem ditas com a mesma frase.
				 */
				filtered: "No results for this filter.",
				denied: "You do not have access to these records.",
				deniedHint: "An administrator of the workspace can grant access.",
				/** `{entity}` é o nome do cadastro, em minúscula: a linha é uma frase. */
				none: "There are no {entity} yet.",
				noneHint: "The records you create appear here.",
			},
			/**
			 * O que uma caixa de referência sugere procurar, por fonte da plataforma.
			 *
			 * Oito nomes fixos. O código de uma entidade do TENANT não está aqui de
			 * propósito: aquele nome é dele e a tela não tem o que melhorar nele.
			 */
			lookup: {
				app_user: "find someone on the team…",
				contact: "find a contact…",
				channel: "find a channel…",
				pipeline: "find a funnel…",
				stage: "find a stage…",
				message_template: "find a message template…",
				artifact: "find a query…",
				schedule: "find a schedule…",
				createRecord: "Create in {entity}",
				createContact: "Create contact “{name}”",
				createHint: "Phone and e-mail are optional.",
				createPhone: "Phone",
				createEmail: "E-mail",
				createSave: "Create",
				createCancel: "Cancel",
				createNeeds: "Enter a name or contact identifier.",
			},
			/** Um cadastro do tenant: `{entity}` é o código que ELE escolheu. */
			lookupIn: "search in {entity}…",
			/**
			 * O QUE UMA CAIXA DE OPÇÕES DIZ QUANDO A CONSULTA POR TRÁS FALHA.
			 *
			 * Não é enfeite: uma seleção vazia e calada não distingue "hoje não há
			 * horário" de "a consulta quebrou", e quem está preenchendo é a única
			 * pessoa que não pode descobrir a diferença sozinha.
			 *
			 * Vive AQUI e não em `features/records/i18n/`, apesar de as frases saírem
			 * de `features/records/api/`: quem as diz são duas funções puras dentro de
			 * um port, sem `setup` e sem `t` — a mesma razão que trouxe os validadores
			 * para este arquivo. `{artifact}` e `{entity}` são códigos do tenant e
			 * nunca se traduzem.
			 */
			optionSource: {
				// O 404 cobre code inexistente E execução sem linhas, e daqui não dá
				// para separar os dois — a frase diz ambos sem afirmar o que não sabe.
				notFound: 'the query "{artifact}" does not exist or does not publish rows yet',
				forbidden: 'you cannot read the query "{artifact}"',
				failed: 'could not read the query "{artifact}"',
				noSuchEntity: 'there is nothing called "{entity}" to search',
				gone: "(deleted record)",
				unnamed: "record",
			},
		},
	},
	"pt-BR": {
		dynamicForm: {
			snippetInsert: "Inserir variável",
			snippetMissing: "Preencha estas variáveis antes de enviar: {variables}",
			snippetVariable: {
				name: "Nome do contato",
				phone: "Telefone do contato",
				email: "E-mail do contato",
				attendant: "Nome do atendente",
				channel: "Nome do canal",
			},
			stage: "Etapa",
			stageHint: "A mesma etapa do funil.",
			required: "{label} é obrigatório",
			invalidEmail: "isso não parece um e-mail",
			invalidPhone: "inclua o código do país com + na frente (um número nacional é ambíguo)",
			invalidUrl: "inclua http:// ou https://",
			invalidTime: "use o formato HH:MM",
			wholeNumber: "use um número inteiro, sem casas decimais",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "escreva o número como em {example}",
			invalidJson: "JSON inválido",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "escolha um momento que ainda está por vir",
			save: "Salvar",
			create: "Criar",
			cancel: "Cancelar",
			loadingOptions: "carregando as opções…",
			optionsUnavailable: "as opções desta pergunta vêm de uma consulta, indisponível aqui",
			uploadUnavailable: "upload não está disponível nesta tela",
			uploadFailed: "não foi possível enviar",
			fileMoveEarlier: "mover para antes (o primeiro é a capa)",
			fileMoveLater: "mover para depois",
			signatureFailed: "não foi possível gerar a imagem",
			signatureSaveFailed: "não foi possível salvar",
			lookupFailed: "não foi possível buscar",
			subformLoadFailed: "não foi possível carregar os itens",
			addChildren: "+ Adicionar {name} agora (opcional)",
			moreDetails: "Mais detalhes",
			computedFrom: "Calculado a partir de {from}",
			computedAuto: "Calculado automaticamente",
			copiedFrom: "copiado de {link}",
			subformAddFailed: "não foi possível adicionar",
			empty: {
				filtered: "Nenhum resultado para este filtro.",
				denied: "Você não tem acesso a estes registros.",
				deniedHint: "Um administrador do espaço pode liberar o acesso.",
				none: "Ainda não há {entity}.",
				noneHint: "Os registros que você criar aparecem aqui.",
			},
			lookup: {
				app_user: "buscar uma pessoa da equipe…",
				contact: "buscar um contato…",
				channel: "buscar um canal…",
				pipeline: "buscar um funil…",
				stage: "buscar uma etapa…",
				message_template: "buscar um modelo de mensagem…",
				artifact: "buscar uma consulta…",
				schedule: "buscar um agendamento…",
				createRecord: "Cadastrar em {entity}",
				createContact: "Criar contato “{name}”",
				createHint: "Telefone e e-mail são opcionais.",
				createPhone: "Telefone",
				createEmail: "E-mail",
				createSave: "Criar",
				createCancel: "Cancelar",
				createNeeds: "Informe um nome ou identificador de contato.",
			},
			lookupIn: "buscar em {entity}…",
			optionSource: {
				notFound: 'a consulta "{artifact}" não existe ou ainda não publica linhas',
				forbidden: 'você não pode ler a consulta "{artifact}"',
				failed: 'não foi possível ler a consulta "{artifact}"',
				noSuchEntity: 'não há nada chamado "{entity}" para buscar',
				gone: "(registro excluído)",
				unnamed: "registro",
			},
		},
	},
	el: {
		dynamicForm: {
			snippetInsert: "Εισαγωγή μεταβλητής",
			snippetMissing: "Συμπλήρωσε αυτές τις μεταβλητές πριν την αποστολή: {variables}",
			snippetVariable: {
				name: "Όνομα επαφής",
				phone: "Τηλέφωνο επαφής",
				email: "Email επαφής",
				attendant: "Όνομα εκπροσώπου",
				channel: "Όνομα καναλιού",
			},
			stage: "Στάδιο",
			stageHint: "Το ίδιο στάδιο με τη διοχέτευση.",
			required: "Το πεδίο {label} είναι υποχρεωτικό",
			invalidEmail: "αυτό δεν μοιάζει με email",
			invalidPhone: "συμπερίλαβε τον κωδικό χώρας με + μπροστά (ένας εθνικός αριθμός είναι ασαφής)",
			invalidUrl: "συμπερίλαβε http:// ή https://",
			invalidTime: "χρησιμοποίησε τη μορφή HH:MM",
			wholeNumber: "χρησιμοποίησε ακέραιο αριθμό, χωρίς δεκαδικά",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "γράψε τον αριθμό όπως στο {example}",
			invalidJson: "μη έγκυρο JSON",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "διάλεξε μια στιγμή που δεν έχει έρθει ακόμη",
			save: "Αποθήκευση",
			create: "Δημιουργία",
			cancel: "Ακύρωση",
			loadingOptions: "φόρτωση των επιλογών…",
			optionsUnavailable:
				"οι επιλογές αυτής της ερώτησης προέρχονται από ένα ερώτημα, μη διαθέσιμο εδώ",
			uploadUnavailable: "η μεταφόρτωση δεν είναι διαθέσιμη σε αυτή την οθόνη",
			uploadFailed: "δεν ήταν δυνατή η μεταφόρτωση",
			fileMoveEarlier: "μετακίνηση νωρίτερα (το πρώτο είναι το εξώφυλλο)",
			fileMoveLater: "μετακίνηση αργότερα",
			signatureFailed: "δεν ήταν δυνατή η δημιουργία της εικόνας",
			signatureSaveFailed: "δεν ήταν δυνατή η αποθήκευση",
			lookupFailed: "δεν ήταν δυνατή η αναζήτηση",
			subformLoadFailed: "δεν ήταν δυνατή η φόρτωση των στοιχείων",
			addChildren: "+ Προσθήκη {name} τώρα (προαιρετικό)",
			moreDetails: "Περισσότερες λεπτομέρειες",
			computedFrom: "Υπολογίζεται από {from}",
			computedAuto: "Υπολογίζεται αυτόματα",
			copiedFrom: "αντιγράφηκε από {link}",
			subformAddFailed: "δεν ήταν δυνατή η προσθήκη",
			empty: {
				filtered: "Κανένα αποτέλεσμα για αυτό το φίλτρο.",
				denied: "Δεν έχεις πρόσβαση σε αυτές τις εγγραφές.",
				deniedHint: "Ένας διαχειριστής του χώρου εργασίας μπορεί να δώσει πρόσβαση.",
				none: "Δεν υπάρχουν {entity} ακόμη.",
				noneHint: "Οι εγγραφές που δημιουργείς εμφανίζονται εδώ.",
			},
			lookup: {
				app_user: "βρες κάποιον από την ομάδα…",
				contact: "βρες μια επαφή…",
				channel: "βρες ένα κανάλι…",
				pipeline: "βρες μια χοάνη…",
				stage: "βρες ένα στάδιο…",
				message_template: "βρες ένα πρότυπο μηνύματος…",
				artifact: "βρες ένα ερώτημα…",
				schedule: "βρες ένα χρονοδιάγραμμα…",
				createRecord: "Δημιουργία στο {entity}",
				createContact: "Δημιουργία επαφής «{name}»",
				createHint: "Το τηλέφωνο και το e-mail είναι προαιρετικά.",
				createPhone: "Τηλέφωνο",
				createEmail: "E-mail",
				createSave: "Δημιουργία",
				createCancel: "Άκυρο",
				createNeeds: "Δώστε ένα όνομα ή αναγνωριστικό επαφής.",
			},
			lookupIn: "αναζήτηση σε {entity}…",
			optionSource: {
				notFound: 'το ερώτημα "{artifact}" δεν υπάρχει ή δεν δημοσιεύει ακόμη γραμμές',
				forbidden: 'δεν μπορείς να διαβάσεις το ερώτημα "{artifact}"',
				failed: 'δεν ήταν δυνατή η ανάγνωση του ερωτήματος "{artifact}"',
				noSuchEntity: 'δεν υπάρχει τίποτα με το όνομα "{entity}" για αναζήτηση',
				gone: "(διαγραμμένη εγγραφή)",
				unnamed: "εγγραφή",
			},
		},
	},
	es: {
		dynamicForm: {
			snippetInsert: "Insertar variable",
			snippetMissing: "Completa estas variables antes de enviar: {variables}",
			snippetVariable: {
				name: "Nombre del contacto",
				phone: "Teléfono del contacto",
				email: "Correo del contacto",
				attendant: "Nombre del agente",
				channel: "Nombre del canal",
			},
			stage: "Etapa",
			stageHint: "La misma etapa del embudo.",
			required: "{label} es obligatorio",
			invalidEmail: "eso no parece un correo electrónico",
			invalidPhone: "incluye el código de país con un + delante (un número nacional es ambiguo)",
			invalidUrl: "incluye http:// o https://",
			invalidTime: "usa el formato HH:MM",
			wholeNumber: "usa un número entero, sin decimales",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "escribe el número como en {example}",
			invalidJson: "JSON no válido",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "elige un momento que aún está por llegar",
			save: "Guardar",
			create: "Crear",
			cancel: "Cancelar",
			loadingOptions: "cargando las opciones…",
			optionsUnavailable:
				"las opciones de esta pregunta vienen de una consulta, no disponible aquí",
			uploadUnavailable: "subir archivos no está disponible en esta pantalla",
			uploadFailed: "no se pudo subir",
			fileMoveEarlier: "mover antes (el primero es la portada)",
			fileMoveLater: "mover después",
			signatureFailed: "no se pudo generar la imagen",
			signatureSaveFailed: "no se pudo guardar",
			lookupFailed: "no se pudo buscar",
			subformLoadFailed: "no se pudieron cargar los elementos",
			addChildren: "+ Añadir {name} ahora (opcional)",
			moreDetails: "Más detalles",
			computedFrom: "Calculado a partir de {from}",
			computedAuto: "Calculado automáticamente",
			copiedFrom: "copiado de {link}",
			subformAddFailed: "no se pudo añadir",
			empty: {
				filtered: "Ningún resultado para este filtro.",
				denied: "No tienes acceso a estos registros.",
				deniedHint: "Un administrador del espacio de trabajo puede dar acceso.",
				none: "Todavía no hay {entity}.",
				noneHint: "Los registros que crees aparecen aquí.",
			},
			lookup: {
				app_user: "buscar a alguien del equipo…",
				contact: "buscar un contacto…",
				channel: "buscar un canal…",
				pipeline: "buscar un embudo…",
				stage: "buscar una etapa…",
				message_template: "buscar una plantilla de mensaje…",
				artifact: "buscar una consulta…",
				schedule: "buscar una programación…",
				createRecord: "Crear en {entity}",
				createContact: "Crear contacto “{name}”",
				createHint: "El teléfono y el e-mail son opcionales.",
				createPhone: "Teléfono",
				createEmail: "E-mail",
				createSave: "Crear",
				createCancel: "Cancelar",
				createNeeds: "Indica un nombre o identificador de contacto.",
			},
			lookupIn: "buscar en {entity}…",
			optionSource: {
				notFound: 'la consulta "{artifact}" no existe o todavía no publica filas',
				forbidden: 'no puedes leer la consulta "{artifact}"',
				failed: 'no se pudo leer la consulta "{artifact}"',
				noSuchEntity: 'no hay nada llamado "{entity}" para buscar',
				gone: "(registro eliminado)",
				unnamed: "registro",
			},
		},
	},
	fr: {
		dynamicForm: {
			snippetInsert: "Insérer une variable",
			snippetMissing: "Renseignez ces variables avant l’envoi : {variables}",
			snippetVariable: {
				name: "Nom du contact",
				phone: "Téléphone du contact",
				email: "E-mail du contact",
				attendant: "Nom du conseiller",
				channel: "Nom du canal",
			},
			stage: "Étape",
			stageHint: "La même étape que dans l’entonnoir.",
			required: "{label} est obligatoire",
			invalidEmail: "cela ne ressemble pas à un e-mail",
			invalidPhone: "indiquez l'indicatif du pays avec un + devant (un numéro national est ambigu)",
			invalidUrl: "indiquez http:// ou https://",
			invalidTime: "utilisez le format HH:MM",
			wholeNumber: "utilisez un nombre entier, sans décimales",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "écrivez le nombre comme dans {example}",
			invalidJson: "JSON non valide",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "choisissez un moment encore à venir",
			save: "Enregistrer",
			create: "Créer",
			cancel: "Annuler",
			loadingOptions: "chargement des options…",
			optionsUnavailable: "les options de cette question viennent d'une requête, indisponible ici",
			uploadUnavailable: "l'envoi de fichiers n'est pas disponible sur cet écran",
			uploadFailed: "envoi impossible",
			fileMoveEarlier: "déplacer avant (le premier est la couverture)",
			fileMoveLater: "déplacer après",
			signatureFailed: "impossible de produire l'image",
			signatureSaveFailed: "enregistrement impossible",
			lookupFailed: "recherche impossible",
			subformLoadFailed: "impossible de charger les éléments",
			addChildren: "+ Ajouter {name} maintenant (facultatif)",
			moreDetails: "Plus de détails",
			computedFrom: "Calculé à partir de {from}",
			computedAuto: "Calculé automatiquement",
			copiedFrom: "copié depuis {link}",
			subformAddFailed: "impossible d'ajouter",
			empty: {
				filtered: "Aucun résultat pour ce filtre.",
				denied: "Vous n'avez pas accès à ces fiches.",
				deniedHint: "Un administrateur de l'espace de travail peut donner l'accès.",
				none: "Il n'y a pas encore de {entity}.",
				noneHint: "Les fiches que vous créez apparaissent ici.",
			},
			lookup: {
				app_user: "trouver quelqu'un de l'équipe…",
				contact: "trouver un contact…",
				channel: "trouver un canal…",
				pipeline: "trouver un pipeline…",
				stage: "trouver une étape…",
				message_template: "trouver un modèle de message…",
				artifact: "trouver une requête…",
				schedule: "trouver une planification…",
				createRecord: "Créer dans {entity}",
				createContact: "Créer le contact « {name} »",
				createHint: "Le téléphone et l’e-mail sont facultatifs.",
				createPhone: "Téléphone",
				createEmail: "E-mail",
				createSave: "Créer",
				createCancel: "Annuler",
				createNeeds: "Indiquez un nom ou un identifiant de contact.",
			},
			lookupIn: "rechercher dans {entity}…",
			optionSource: {
				notFound: "la requête « {artifact} » n'existe pas ou ne publie pas encore de lignes",
				forbidden: "vous ne pouvez pas lire la requête « {artifact} »",
				failed: "impossible de lire la requête « {artifact} »",
				noSuchEntity: "il n'y a rien qui s'appelle « {entity} » à rechercher",
				gone: "(enregistrement supprimé)",
				unnamed: "enregistrement",
			},
		},
	},
	it: {
		dynamicForm: {
			snippetInsert: "Inserisci variabile",
			snippetMissing: "Compila queste variabili prima di inviare: {variables}",
			snippetVariable: {
				name: "Nome del contatto",
				phone: "Telefono del contatto",
				email: "Email del contatto",
				attendant: "Nome dell’operatore",
				channel: "Nome del canale",
			},
			stage: "Fase",
			stageHint: "La stessa fase del funnel.",
			required: "{label} è obbligatorio",
			invalidEmail: "questo non sembra un'email",
			invalidPhone:
				"includi il prefisso internazionale con il + davanti (un numero nazionale è ambiguo)",
			invalidUrl: "includi http:// o https://",
			invalidTime: "usa il formato HH:MM",
			wholeNumber: "usa un numero intero, senza decimali",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "scrivi il numero come in {example}",
			invalidJson: "JSON non valido",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "scegli un momento ancora a venire",
			save: "Salva",
			create: "Crea",
			cancel: "Annulla",
			loadingOptions: "caricamento delle opzioni…",
			optionsUnavailable: "le opzioni di questa domanda arrivano da una query, non disponibile qui",
			uploadUnavailable: "il caricamento non è disponibile in questa schermata",
			uploadFailed: "non è stato possibile caricare",
			fileMoveEarlier: "sposta prima (il primo è la copertina)",
			fileMoveLater: "sposta dopo",
			signatureFailed: "non è stato possibile generare l'immagine",
			signatureSaveFailed: "non è stato possibile salvare",
			lookupFailed: "non è stato possibile cercare",
			subformLoadFailed: "non è stato possibile caricare gli elementi",
			addChildren: "+ Aggiungi {name} ora (facoltativo)",
			moreDetails: "Altri dettagli",
			computedFrom: "Calcolato a partire da {from}",
			computedAuto: "Calcolato automaticamente",
			copiedFrom: "copiato da {link}",
			subformAddFailed: "non è stato possibile aggiungere",
			empty: {
				filtered: "Nessun risultato per questo filtro.",
				denied: "Non hai accesso a questi record.",
				deniedHint: "Un amministratore dello spazio di lavoro può concedere l'accesso.",
				none: "Non ci sono ancora {entity}.",
				noneHint: "I record che crei compaiono qui.",
			},
			lookup: {
				app_user: "trova qualcuno del team…",
				contact: "trova un contatto…",
				channel: "trova un canale…",
				pipeline: "trova una pipeline…",
				stage: "trova una fase…",
				message_template: "trova un modello di messaggio…",
				artifact: "trova una query…",
				schedule: "trova una pianificazione…",
				createRecord: "Crea in {entity}",
				createContact: "Crea contatto “{name}”",
				createHint: "Telefono ed e-mail sono facoltativi.",
				createPhone: "Telefono",
				createEmail: "E-mail",
				createSave: "Crea",
				createCancel: "Annulla",
				createNeeds: "Indica un nome o identificativo di contatto.",
			},
			lookupIn: "cerca in {entity}…",
			optionSource: {
				notFound: "la query «{artifact}» non esiste o non pubblica ancora righe",
				forbidden: "non puoi leggere la query «{artifact}»",
				failed: "non è stato possibile leggere la query «{artifact}»",
				noSuchEntity: "non c'è niente che si chiami «{entity}» da cercare",
				gone: "(record eliminato)",
				unnamed: "record",
			},
		},
	},
	de: {
		dynamicForm: {
			snippetInsert: "Variable einfügen",
			snippetMissing: "Fülle diese Variablen vor dem Senden aus: {variables}",
			snippetVariable: {
				name: "Kontaktname",
				phone: "Telefon des Kontakts",
				email: "E-Mail des Kontakts",
				attendant: "Name der Betreuungsperson",
				channel: "Kanalname",
			},
			stage: "Phase",
			stageHint: "Dieselbe Phase wie im Funnel.",
			required: "{label} ist ein Pflichtfeld",
			invalidEmail: "das sieht nicht nach einer E-Mail aus",
			invalidPhone: "gib die Ländervorwahl mit + davor an (eine nationale Nummer ist mehrdeutig)",
			invalidUrl: "gib http:// oder https:// an",
			invalidTime: "verwende das Format HH:MM",
			wholeNumber: "verwende eine ganze Zahl ohne Nachkommastellen",
			/** Um número que não se lê sem dúvida na língua do workspace; `{example}` é um válido. */
			unreadNumber: "schreibe die Zahl wie in {example}",
			invalidJson: "ungültiges JSON",
			/** O campo que pede o futuro (`config.future`): "quando enviar" não aceita ontem. */
			mustBeFuture: "wähle einen Zeitpunkt, der noch bevorsteht",
			save: "Speichern",
			create: "Anlegen",
			cancel: "Abbrechen",
			loadingOptions: "Optionen werden geladen…",
			optionsUnavailable:
				"die Optionen dieser Frage kommen aus einer Abfrage, hier nicht verfügbar",
			uploadUnavailable: "Hochladen ist auf dieser Maske nicht verfügbar",
			uploadFailed: "Hochladen nicht möglich",
			fileMoveEarlier: "nach vorne (das erste ist das Titelbild)",
			fileMoveLater: "nach hinten",
			signatureFailed: "Bild konnte nicht erzeugt werden",
			signatureSaveFailed: "Speichern nicht möglich",
			lookupFailed: "Suche nicht möglich",
			subformLoadFailed: "Einträge konnten nicht geladen werden",
			addChildren: "+ {name} jetzt hinzufügen (optional)",
			moreDetails: "Weitere Details",
			computedFrom: "Berechnet aus {from}",
			computedAuto: "Automatisch berechnet",
			copiedFrom: "übernommen aus {link}",
			subformAddFailed: "Hinzufügen nicht möglich",
			empty: {
				filtered: "Keine Ergebnisse für diesen Filter.",
				denied: "Du hast keinen Zugriff auf diese Datensätze.",
				deniedHint: "Ein Administrator des Workspace kann den Zugriff freigeben.",
				none: "Es gibt noch keine {entity}.",
				noneHint: "Die Datensätze, die du anlegst, erscheinen hier.",
			},
			lookup: {
				app_user: "jemanden aus dem Team finden…",
				contact: "einen Kontakt finden…",
				channel: "einen Kanal finden…",
				pipeline: "eine Pipeline finden…",
				stage: "eine Phase finden…",
				message_template: "eine Nachrichtenvorlage finden…",
				artifact: "eine Abfrage finden…",
				schedule: "eine Planung finden…",
				createRecord: "In {entity} erstellen",
				createContact: "Kontakt „{name}“ anlegen",
				createHint: "Telefon und E-Mail sind optional.",
				createPhone: "Telefon",
				createEmail: "E-Mail",
				createSave: "Anlegen",
				createCancel: "Abbrechen",
				createNeeds: "Geben Sie einen Namen oder eine Kontaktkennung an.",
			},
			lookupIn: "in {entity} suchen…",
			optionSource: {
				notFound: "die Abfrage „{artifact}“ gibt es nicht oder sie liefert noch keine Zeilen",
				forbidden: "du darfst die Abfrage „{artifact}“ nicht lesen",
				failed: "die Abfrage „{artifact}“ konnte nicht gelesen werden",
				noSuchEntity: "es gibt nichts mit dem Namen „{entity}“ zum Suchen",
				gone: "(gelöschter Eintrag)",
				unnamed: "Eintrag",
			},
		},
	},
} as const;
